import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertTriangle, CalendarClock, Check, CheckCircle2, FileSpreadsheet, FileText,
  History, Loader, Mail, Pencil, Plus, Send, Trash2, X
} from 'lucide-react'
import { apiClient } from '../../config/api'
import SearchableSelect from '../common/SearchableSelect'
import { reportVehicleOption } from './vehicleOption'
import type { ReportDescriptor, ReportSchedule, ReportScope } from './reportTypes'

interface FleetOption { id: number; name: string; customer_name?: string | null }
interface VehicleOption {
  vehicle_id: number
  vehicle_source: 'ASSET' | 'CONTAINER'
  vehicle_code?: string | null
  license_plate?: string | null
  custom_name?: string | null
}

interface ScheduleRunRow {
  id: number
  scheduled_for: string
  status: string
  attempt_count: number
  recipient_count: number | null
  delivered_count: number | null
  error_message: string | null
  completed_at: string | null
}

interface CustomerOption { id: number; company_name: string; customer_code?: string | null }
interface DriverOption { id: number; name: string; staffCode?: string | null }

interface ReportScheduleManagerProps {
  reports: ReportDescriptor[]
  periods: string[]
  formats: string[]
  fleets: FleetOption[]
  vehicles: VehicleOption[]
  drivers?: DriverOption[]
  /** Only populated for platform accounts, which may aim a schedule at one customer. */
  customers?: CustomerOption[]
  canChooseCustomer?: boolean
  showMessage?: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void
  showConfirm?: (message: string) => Promise<boolean>
}

type Draft = {
  id: number | null
  name: string
  description: string
  reportIds: string[]
  formats: string[]
  frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY'
  windowPeriod: string
  sendHour: number
  sendMinute: number
  sendWeekdays: number[]
  sendDaysOfMonth: number[]
  groupedSend: boolean
  timezone: string
  locale: 'en' | 'fr'
  subject: string
  body: string
  recipients: string
  scopeKind: ReportScope['kind']
  fleetId: string
  vehicleRef: string
  driverId: string
  customerId: string
  isActive: boolean
}

const inputCls = 'w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none'
const labelCls = 'block text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1'
const cardCls = 'rounded-xl border border-gray-200 bg-white'

// "PDF" alone does not say what arrives in the mailbox; the label does.
const FORMAT_META: Record<string, { Icon: typeof FileText; labelKey: string }> = {
  pdf: { Icon: FileText, labelKey: 'reports.format.pdf' },
  xlsx: { Icon: FileSpreadsheet, labelKey: 'reports.format.xlsx' },
  csv: { Icon: FileText, labelKey: 'reports.format.csv' }
}

// Sending times are set in a zone, and the fleets run in West Africa, so those
// zones come first and GMT is the default. A free-text field here produced
// "Europe/Paris" from the browser and an hour of offset nobody asked for.
const TIMEZONES: Array<{ value: string; label: string }> = [
  { value: 'UTC', label: 'GMT (UTC+00)' },
  { value: 'Africa/Bamako', label: 'Bamako — Mali (UTC+00)' },
  { value: 'Africa/Dakar', label: 'Dakar — Senegal (UTC+00)' },
  { value: 'Africa/Abidjan', label: "Abidjan — Côte d'Ivoire (UTC+00)" },
  { value: 'Africa/Conakry', label: 'Conakry — Guinea (UTC+00)' },
  { value: 'Africa/Ouagadougou', label: 'Ouagadougou — Burkina Faso (UTC+00)' },
  { value: 'Africa/Lome', label: 'Lomé — Togo (UTC+00)' },
  { value: 'Africa/Accra', label: 'Accra — Ghana (UTC+00)' },
  { value: 'Africa/Nouakchott', label: 'Nouakchott — Mauritania (UTC+00)' },
  { value: 'Africa/Lagos', label: 'Lagos — Nigeria (UTC+01)' },
  { value: 'Africa/Niamey', label: 'Niamey — Niger (UTC+01)' },
  { value: 'Africa/Douala', label: 'Douala — Cameroon (UTC+01)' },
  { value: 'Africa/Casablanca', label: 'Casablanca — Morocco (UTC+01)' },
  { value: 'Europe/Paris', label: 'Paris — France (UTC+01/+02)' }
]

const emptyDraft = (locale: 'en' | 'fr'): Draft => ({
  id: null,
  name: '',
  description: '',
  reportIds: [],
  formats: ['pdf'],
  frequency: 'WEEKLY',
  windowPeriod: 'LAST_7_DAYS',
  sendHour: 6,
  sendMinute: 0,
  sendWeekdays: [1],
  sendDaysOfMonth: [1],
  groupedSend: true,
  timezone: 'UTC',
  locale,
  subject: '',
  body: '',
  recipients: '',
  scopeKind: 'customer',
  fleetId: '',
  vehicleRef: '',
  driverId: '',
  customerId: '',
  isActive: true
})

const draftFromSchedule = (schedule: ReportSchedule): Draft => ({
  id: schedule.id,
  name: schedule.name,
  description: schedule.description || '',
  reportIds: schedule.reportIds || [],
  formats: schedule.formats?.length ? schedule.formats : ['pdf'],
  frequency: schedule.frequency,
  windowPeriod: schedule.windowPeriod,
  sendHour: schedule.sendHour ?? 6,
  sendMinute: schedule.sendMinute ?? 0,
  sendWeekdays: schedule.sendWeekdays?.length ? schedule.sendWeekdays : [schedule.sendWeekday ?? 1],
  sendDaysOfMonth: schedule.sendDaysOfMonth?.length ? schedule.sendDaysOfMonth : [schedule.sendDayOfMonth ?? 1],
  groupedSend: schedule.groupedSend !== false,
  timezone: schedule.timezone || 'UTC',
  locale: schedule.locale === 'fr' ? 'fr' : 'en',
  subject: schedule.subject || '',
  body: schedule.body || '',
  recipients: (schedule.recipientEmails || []).join(', '),
  scopeKind: schedule.scope?.kind || 'customer',
  fleetId: schedule.scope?.fleetId ? String(schedule.scope.fleetId) : '',
  vehicleRef: schedule.scope?.vehicleId
    ? `${schedule.scope.vehicleSource}:${schedule.scope.vehicleId}`
    : '',
  driverId: schedule.scope?.driverId ? String(schedule.scope.driverId) : '',
  customerId: schedule.customerId == null ? '' : String(schedule.customerId),
  isActive: schedule.isActive
})

const ReportScheduleManager: React.FC<ReportScheduleManagerProps> = ({
  reports, periods, formats, fleets, vehicles, drivers = [],
  customers = [], canChooseCustomer = false, showMessage, showConfirm
}) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.language?.startsWith('fr') ? 'fr' : 'en'

  const customerOptions = useMemo(() => customers.map(customer => ({
    value: String(customer.id),
    label: customer.company_name,
    hint: customer.customer_code || null
  })), [customers])

  const driverOptions = useMemo(() => drivers.map(driver => ({
    value: String(driver.id),
    label: driver.name,
    hint: driver.staffCode || null
  })), [drivers])

  const fleetOptions = useMemo(() => fleets.map(fleet => ({
    value: String(fleet.id),
    label: fleet.name,
    hint: fleet.customer_name || null
  })), [fleets])

  const vehicleOptions = useMemo(() => vehicles.map(reportVehicleOption), [vehicles])

  const [schedules, setSchedules] = useState<ReportSchedule[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [saving, setSaving] = useState(false)
  const [sendingNow, setSendingNow] = useState<number | null>(null)
  const [runsFor, setRunsFor] = useState<number | null>(null)
  const [runs, setRuns] = useState<ScheduleRunRow[]>([])

  // An existing schedule may have been saved in a zone that is not on the list
  // (the old field accepted anything); keeping it prevents an edit from silently
  // moving its send time.
  const timezoneChoices = useMemo(() => {
    const saved = draft?.timezone
    if (!saved || TIMEZONES.some(zone => zone.value === saved)) return TIMEZONES
    return [...TIMEZONES, { value: saved, label: saved }]
  }, [draft?.timezone])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await apiClient.get('/report-schedules')
      setSchedules(response.data?.schedules || [])
    } catch (loadError: any) {
      setError(loadError?.message || 'Failed to load schedules')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const toggle = (list: string[], value: string) =>
    list.includes(value) ? list.filter(item => item !== value) : [...list, value]

  const save = async () => {
    if (!draft) return
    setSaving(true)
    try {
      const scope: ReportScope = { kind: draft.scopeKind }
      if (draft.scopeKind === 'fleet') scope.fleetId = Number(draft.fleetId)
      if (draft.scopeKind === 'vehicle') {
        const [source, id] = draft.vehicleRef.split(':')
        scope.vehicleSource = source as 'ASSET' | 'CONTAINER'
        scope.vehicleId = Number(id)
      }
      if (draft.scopeKind === 'driver') scope.driverId = Number(draft.driverId)
      const payload = {
        name: draft.name,
        description: draft.description || null,
        report_ids: draft.reportIds,
        formats: draft.formats,
        frequency: draft.frequency,
        window_period: draft.windowPeriod,
        send_hour: Number(draft.sendHour),
        send_minute: Number(draft.sendMinute),
        send_weekdays: draft.frequency === 'WEEKLY' ? draft.sendWeekdays : undefined,
        send_days_of_month: draft.frequency === 'MONTHLY' ? draft.sendDaysOfMonth : undefined,
        grouped_send: draft.groupedSend,
        timezone: draft.timezone,
        locale: draft.locale,
        subject: draft.subject || draft.name,
        body: draft.body,
        recipient_emails: draft.recipients.split(/[,;\s]+/).map(item => item.trim()).filter(Boolean),
        scope,
        // Ignored server-side for tenant accounts, which are pinned to their own
        // customer whatever the body says.
        customer_id: draft.customerId || undefined,
        is_active: draft.isActive
      }
      if (draft.id) await apiClient.put(`/report-schedules/${draft.id}`, payload)
      else await apiClient.post('/report-schedules', payload)
      showMessage?.(t('reports.schedules.saved'), 'success')
      setDraft(null)
      load()
    } catch (saveError: any) {
      showMessage?.(saveError?.message || t('reports.schedules.saveFailed'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const remove = async (schedule: ReportSchedule) => {
    const confirmed = showConfirm
      ? await showConfirm(t('reports.schedules.deleteConfirm', { name: schedule.name }))
      : window.confirm(t('reports.schedules.deleteConfirm', { name: schedule.name }))
    if (!confirmed) return
    try {
      await apiClient.delete(`/report-schedules/${schedule.id}`)
      showMessage?.(t('reports.schedules.deleted'), 'success')
      load()
    } catch (deleteError: any) {
      showMessage?.(deleteError?.message || t('reports.schedules.deleteFailed'), 'error')
    }
  }

  const sendNow = async (schedule: ReportSchedule) => {
    setSendingNow(schedule.id)
    try {
      const response = await apiClient.post(`/report-schedules/${schedule.id}/run-now`, {})
      const outcome = response.data || {}
      if (outcome.status === 'SENT') {
        showMessage?.(t('reports.schedules.sentTo', { count: Number(outcome.recipients ?? 0) }), 'success')
      } else if (outcome.status === 'SKIPPED') {
        showMessage?.(t('reports.schedules.skippedNoRecipient'), 'warning')
      } else {
        showMessage?.(t('reports.schedules.sendFailedCode', { code: outcome.error || outcome.status }), 'error')
      }
      load()
      if (runsFor === schedule.id) openRuns(schedule.id)
    } catch (sendError: any) {
      showMessage?.(sendError?.message || t('reports.schedules.sendFailed'), 'error')
    } finally {
      setSendingNow(null)
    }
  }

  const openRuns = async (scheduleId: number) => {
    setRunsFor(scheduleId)
    setRuns([])
    try {
      const response = await apiClient.get(`/report-schedules/${scheduleId}/runs`)
      setRuns(response.data?.runs || [])
    } catch {
      setRuns([])
    }
  }

  const describe = (schedule: ReportSchedule) => {
    const time = `${String(schedule.sendHour).padStart(2, '0')}:${String(schedule.sendMinute).padStart(2, '0')}`
    if (schedule.frequency === 'DAILY') return t('reports.schedules.everyDayAt', { time })
    if (schedule.frequency === 'WEEKLY') {
      const days = schedule.sendWeekdays?.length ? schedule.sendWeekdays : [schedule.sendWeekday ?? 1]
      return t('reports.schedules.everyWeekAt', {
        day: days.map(day => t(`reports.weekday.${day}`)).join(', '),
        time
      })
    }
    const days = schedule.sendDaysOfMonth?.length ? schedule.sendDaysOfMonth : [schedule.sendDayOfMonth ?? 1]
    return t('reports.schedules.everyMonthAt', { day: days.join(', '), time })
  }

  const toggleIn = (list: number[], value: number) => {
    const next = list.includes(value) ? list.filter(item => item !== value) : [...list, value]
    return next.sort((a, b) => a - b)
  }

  const draftValid = Boolean(
    draft &&
    draft.name.trim() &&
    draft.reportIds.length &&
    draft.formats.length &&
    draft.recipients.trim() &&
    (draft.scopeKind !== 'fleet' || draft.fleetId) &&
    (draft.scopeKind !== 'vehicle' || draft.vehicleRef)
  )

  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-4">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-base font-bold text-gray-800">
            <CalendarClock size={16} className="text-blue-600" />{t('reports.schedules.title')}
          </h3>
          <p className="mt-0.5 text-xs text-gray-500">{t('reports.schedules.subtitle')}</p>
        </div>
        <button
          onClick={() => setDraft(emptyDraft(locale))}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-bold text-white"
        >
          <Plus size={14} />{t('reports.schedules.new')}
        </button>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />{error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 p-6 text-sm text-gray-500">
          <Loader size={16} className="animate-spin" />{t('loading')}
        </div>
      ) : schedules.length === 0 && !draft ? (
        <div className={`${cardCls} p-8 text-center`}>
          <Mail size={28} className="mx-auto mb-3 text-gray-300" />
          <p className="text-sm font-semibold text-gray-600">{t('reports.schedules.emptyTitle')}</p>
          <p className="mt-1 text-xs text-gray-500">{t('reports.schedules.emptyHint')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {schedules.map(schedule => (
            <div key={schedule.id} className={`${cardCls} p-4`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 text-sm font-bold text-gray-800">
                    {schedule.name}
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                      schedule.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {schedule.isActive ? t('enabled') : t('disabled')}
                    </span>
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    {describe(schedule)} · {schedule.timezone} · {t(`reports.periods.${schedule.windowPeriod}`)}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    {schedule.reportIds
                      .map(id => reports.find(report => report.id === id)?.label || id)
                      .join(', ')}
                    {' · '}{schedule.formats.map(format => format.toUpperCase()).join(', ')}
                  </p>
                  <p className="mt-1 text-xs text-gray-400">
                    {t('reports.schedules.recipients')}: {schedule.recipientEmails.join(', ') || '—'}
                  </p>
                  {schedule.nextRunAt && (
                    <p className="mt-1 text-xs font-semibold text-blue-700">
                      {t('reports.schedules.nextRun')}: {new Date(schedule.nextRunAt).toLocaleString(locale)}
                    </p>
                  )}
                </div>
                <div className="flex flex-shrink-0 items-center gap-1">
                  <button
                    onClick={() => sendNow(schedule)}
                    disabled={sendingNow === schedule.id}
                    title={t('reports.schedules.sendNow')}
                    className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {sendingNow === schedule.id ? <Loader size={13} className="animate-spin" /> : <Send size={13} />}
                    {t('reports.schedules.sendNow')}
                  </button>
                  <button
                    onClick={() => openRuns(schedule.id)}
                    title={t('reports.schedules.history')}
                    className="rounded-lg border border-gray-300 p-1.5 text-gray-600 hover:bg-gray-50"
                  >
                    <History size={13} />
                  </button>
                  <button
                    onClick={() => setDraft(draftFromSchedule(schedule))}
                    className="rounded-lg border border-gray-300 p-1.5 text-gray-600 hover:bg-gray-50"
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    onClick={() => remove(schedule)}
                    className="rounded-lg border border-red-200 p-1.5 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>

              {runsFor === schedule.id && (
                <div className="mt-3 border-t border-gray-100 pt-3">
                  <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-400">
                    {t('reports.schedules.history')}
                  </p>
                  {runs.length === 0 ? (
                    <p className="text-xs italic text-gray-500">{t('reports.schedules.noRuns')}</p>
                  ) : (
                    <table className="min-w-full text-left text-xs">
                      <thead>
                        <tr className="text-gray-500">
                          <th className="py-1 pr-4 font-semibold">{t('reports.schedules.scheduledFor')}</th>
                          <th className="py-1 pr-4 font-semibold">{t('status', { defaultValue: 'Status' })}</th>
                          <th className="py-1 pr-4 font-semibold">{t('reports.schedules.recipients')}</th>
                          <th className="py-1 font-semibold">{t('reports.schedules.detail')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {runs.map(item => (
                          <tr key={item.id} className="border-t border-gray-100">
                            <td className="py-1 pr-4 text-gray-700">{new Date(item.scheduled_for).toLocaleString(locale)}</td>
                            <td className="py-1 pr-4">
                              <span className={`inline-flex items-center gap-1 font-semibold ${
                                item.status === 'SENT' ? 'text-emerald-700'
                                  : item.status === 'FAILED' ? 'text-red-600' : 'text-gray-600'
                              }`}>
                                {item.status === 'SENT' && <CheckCircle2 size={11} />}
                                {item.status}
                              </span>
                            </td>
                            <td className="py-1 pr-4 text-gray-600">
                              {item.delivered_count ?? item.recipient_count ?? '—'}
                            </td>
                            <td className="py-1 text-gray-500">{item.error_message || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {draft && (
        <div className={`${cardCls} mt-4 p-4`}>
          <div className="mb-3 flex items-center justify-between">
            <h4 className="text-sm font-bold text-gray-800">
              {draft.id ? t('reports.schedules.editTitle') : t('reports.schedules.newTitle')}
            </h4>
            <button onClick={() => setDraft(null)} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100">
              <X size={15} />
            </button>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className={labelCls}>{t('reports.schedules.name')}</label>
              <input value={draft.name} onChange={event => setDraft({ ...draft, name: event.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>{t('reports.schedules.subject')}</label>
              <input
                value={draft.subject}
                placeholder={draft.name}
                onChange={event => setDraft({ ...draft, subject: event.target.value })}
                className={inputCls}
              />
            </div>
          </div>

          <div className="mt-3">
            <label className={labelCls}>{t('reports.schedules.reports')}</label>
            <div className="flex flex-wrap gap-1.5">
              {reports.map(report => (
                <button
                  key={report.id}
                  type="button"
                  onClick={() => setDraft({ ...draft, reportIds: toggle(draft.reportIds, report.id) })}
                  className={`rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${
                    draft.reportIds.includes(report.id)
                      ? 'border-blue-400 bg-blue-50 text-blue-700'
                      : 'border-gray-200 text-gray-600'
                  }`}
                >
                  {report.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className={labelCls}>{t('reports.schedules.formats')}</label>
              <div className="flex flex-wrap gap-1.5">
                {formats.map(format => {
                  const meta = FORMAT_META[format] || { Icon: FileText, labelKey: '' }
                  const checked = draft.formats.includes(format)
                  return (
                    <button
                      key={format}
                      type="button"
                      aria-pressed={checked}
                      onClick={() => setDraft({ ...draft, formats: toggle(draft.formats, format) })}
                      className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-colors ${
                        checked
                          ? 'border-blue-500 bg-blue-600 text-white'
                          : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {checked ? <Check size={13} /> : <meta.Icon size={13} />}
                      {meta.labelKey ? t(meta.labelKey) : format.toUpperCase()}
                    </button>
                  )
                })}
              </div>
              {draft.formats.length === 0 && (
                <p className="mt-1 text-[11px] text-amber-700">{t('reports.schedules.pickFormat')}</p>
              )}
            </div>

            <div>
              <label className={labelCls}>{t('reports.schedules.frequency')}</label>
              <select
                value={draft.frequency}
                onChange={event => setDraft({ ...draft, frequency: event.target.value as Draft['frequency'] })}
                className={inputCls}
              >
                <option value="DAILY">{t('reports.schedules.daily')}</option>
                <option value="WEEKLY">{t('reports.schedules.weekly')}</option>
                <option value="MONTHLY">{t('reports.schedules.monthly')}</option>
              </select>
            </div>

            {/* Several days may be ticked: "Monday and Friday", "the 1st and
                the 15th". The week starts on Monday as the customer reads it. */}
            {draft.frequency === 'WEEKLY' && (
              <div className="md:col-span-2">
                <label className={labelCls}>{t('reports.schedules.weekdays', { defaultValue: 'Days of the week' })}</label>
                <div className="flex flex-wrap gap-1.5">
                  {[1, 2, 3, 4, 5, 6, 0].map(day => {
                    const on = draft.sendWeekdays.includes(day)
                    return (
                      <button
                        key={day}
                        type="button"
                        onClick={() => setDraft({ ...draft, sendWeekdays: toggleIn(draft.sendWeekdays, day) })}
                        className={`rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${
                          on ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                        aria-pressed={on}
                      >
                        {t(`reports.weekday.${day}`)}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {draft.frequency === 'MONTHLY' && (
              <div className="md:col-span-2">
                <label className={labelCls}>{t('reports.schedules.daysOfMonth', { defaultValue: 'Days of the month' })}</label>
                <div className="flex flex-wrap gap-1">
                  {Array.from({ length: 28 }, (_, index) => index + 1).map(day => {
                    const on = draft.sendDaysOfMonth.includes(day)
                    return (
                      <button
                        key={day}
                        type="button"
                        onClick={() => setDraft({ ...draft, sendDaysOfMonth: toggleIn(draft.sendDaysOfMonth, day) })}
                        className={`h-8 w-8 rounded-lg border text-xs font-semibold ${
                          on ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                        aria-pressed={on}
                      >
                        {day}
                      </button>
                    )
                  })}
                </div>
                <p className="mt-1 text-[11px] text-gray-500">
                  {t('reports.schedules.daysOfMonthHint', { defaultValue: 'Up to the 28th, so the report also goes out in February.' })}
                </p>
              </div>
            )}

            <div>
              <label className={labelCls}>{t('reports.schedules.sendTime')}</label>
              <input
                type="time"
                value={`${String(draft.sendHour).padStart(2, '0')}:${String(draft.sendMinute).padStart(2, '0')}`}
                onChange={event => {
                  const [hour, minute] = event.target.value.split(':')
                  setDraft({ ...draft, sendHour: Number(hour) || 0, sendMinute: Number(minute) || 0 })
                }}
                className={inputCls}
              />
            </div>

            <div>
              <label className={labelCls}>{t('reports.schedules.timezone')}</label>
              <select
                value={draft.timezone}
                onChange={event => setDraft({ ...draft, timezone: event.target.value })}
                className={inputCls}
              >
                {timezoneChoices.map(zone => (
                  <option key={zone.value} value={zone.value}>{zone.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelCls}>{t('reports.periodLabel')}</label>
              <select
                value={draft.windowPeriod}
                onChange={event => setDraft({ ...draft, windowPeriod: event.target.value })}
                className={inputCls}
              >
                {periods.map(item => (
                  <option key={item} value={item}>{t(`reports.periods.${item}`)}</option>
                ))}
              </select>
            </div>

            {canChooseCustomer && (
              <div>
                <label className={labelCls}>{t('reports.customer')}</label>
                <SearchableSelect
                  options={customerOptions}
                  value={draft.customerId}
                  onChange={customerId => setDraft({ ...draft, customerId })}
                  placeholder={t('reports.allCustomers')}
                  searchPlaceholder={t('reports.searchCustomer')}
                  emptyText={t('reports.noMatch')}
                  clearText={t('reports.allCustomers')}
                />
              </div>
            )}

            <div>
              <label className={labelCls}>{t('reports.scope')}</label>
              <select
                value={draft.scopeKind}
                onChange={event => setDraft({ ...draft, scopeKind: event.target.value as ReportScope['kind'] })}
                className={inputCls}
              >
                <option value="customer">{t('reports.scopeCustomer')}</option>
                <option value="fleet">{t('reports.scopeFleet')}</option>
                <option value="vehicle">{t('reports.scopeVehicle')}</option>
                <option value="driver">{t('reports.scopeDriver')}</option>
              </select>
            </div>

            {draft.scopeKind === 'fleet' && (
              <div>
                <label className={labelCls}>{t('reports.fleet')}</label>
                <SearchableSelect
                  options={fleetOptions}
                  value={draft.fleetId}
                  onChange={fleetId => setDraft({ ...draft, fleetId })}
                  placeholder={t('reports.choose')}
                  searchPlaceholder={t('reports.searchFleet')}
                  emptyText={t('reports.noMatch')}
                />
              </div>
            )}

            {draft.scopeKind === 'vehicle' && (
              <div>
                <label className={labelCls}>{t('reports.vehicle')}</label>
                <SearchableSelect
                  options={vehicleOptions}
                  value={draft.vehicleRef}
                  onChange={vehicleRef => setDraft({ ...draft, vehicleRef })}
                  placeholder={t('reports.choose')}
                  searchPlaceholder={t('reports.searchVehicle')}
                  emptyText={t('reports.noMatch')}
                />
              </div>
            )}

            {draft.scopeKind === 'driver' && (
              <div>
                <label className={labelCls}>{t('reports.driver')}</label>
                <SearchableSelect
                  options={driverOptions}
                  value={draft.driverId}
                  onChange={driverId => setDraft({ ...draft, driverId })}
                  placeholder={t('reports.choose')}
                  searchPlaceholder={t('reports.searchDriver')}
                  emptyText={t('reports.noDrivers')}
                />
              </div>
            )}

            <div>
              <label className={labelCls}>{t('reports.schedules.mailLanguage')}</label>
              <select
                value={draft.locale}
                onChange={event => setDraft({ ...draft, locale: event.target.value as 'en' | 'fr' })}
                className={inputCls}
              >
                <option value="en">English</option>
                <option value="fr">Français</option>
              </select>
            </div>
          </div>

          <div className="mt-3">
            <label className={labelCls}>{t('reports.schedules.recipients')}</label>
            <input
              value={draft.recipients}
              placeholder="ops@example.com, direction@example.com"
              onChange={event => setDraft({ ...draft, recipients: event.target.value })}
              className={inputCls}
            />
            <label className="mt-2 flex items-start gap-2 text-xs text-gray-700">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={draft.groupedSend}
                onChange={event => setDraft({ ...draft, groupedSend: event.target.checked })}
              />
              <span>
                <span className="font-semibold">{t('reports.schedules.groupedSend', { defaultValue: 'Grouped send' })}</span>
                {' — '}
                {draft.groupedSend
                  ? t('reports.schedules.groupedSendOn', { defaultValue: 'one mail to all addresses; recipients see each other.' })
                  : t('reports.schedules.groupedSendOff', { defaultValue: 'one separate mail per address.' })}
              </span>
            </label>
          </div>

          <div className="mt-3">
            <label className={labelCls}>{t('reports.schedules.body')}</label>
            <textarea
              value={draft.body}
              rows={3}
              onChange={event => setDraft({ ...draft, body: event.target.value })}
              className={inputCls}
              placeholder={t('reports.schedules.bodyPlaceholder')}
            />
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={draft.isActive}
                onChange={event => setDraft({ ...draft, isActive: event.target.checked })}
              />
              {t('reports.schedules.active')}
            </label>
            <div className="flex items-center gap-2">
              <button onClick={() => setDraft(null)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700">
                {t('cancel')}
              </button>
              <button
                onClick={save}
                disabled={!draftValid || saving}
                className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
              >
                {saving && <Loader size={14} className="animate-spin" />}{t('save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ReportScheduleManager
